import { useCallback } from "react";
import { usePreferences } from "@/hooks/usePreferences";
import type { FitMode } from "../state/types";

/**
 * R-198 — Persist the operator's chosen fit mode ("width" | "page") to
 * OperatorPreference.settings.viewerFitMode so it survives doc/batch
 * transitions, logout, and multiple tabs/devices.
 *
 * Mirrors useViewerZoomPersist (R-149): returns a `persistFitMode(mode)`
 * callback to call from the explicit fit-mode toggle. usePreferences already
 * applies its own 300ms debounce + optimistic localStorage write, so no extra
 * debounce is needed here — a fit-mode toggle is a discrete click, not a
 * stream of wheel ticks.
 *
 * On the read side, useApplyPreferences seeds the store's fitMode from the
 * persisted value on mount, and DocumentCanvas's bitmap-load effect fits
 * using that mode (R-197).
 */
export function useViewerFitModePersist(): (mode: FitMode) => void {
  const { updatePreference } = usePreferences();

  return useCallback(
    (mode: FitMode) => {
      updatePreference("viewerFitMode", mode);
    },
    [updatePreference],
  );
}
